import { NgModule } from '@angular/core';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloLink } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import {
    CheckableRowsServiceFactory,
    ErrorMessageHelper,
    LsqTablesModule
} from '@lsq-frontend/common-ui';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import { DrApplicationSharedComponentsModule } from '@lsqlabs/dr-application-shared';
import { ApolloModule, APOLLO_NAMED_OPTIONS, NamedOptions } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { AppCommonModule } from '../app-common.module';
import { SettingsModule } from '../settings/settings.module';
import { AdminRoutingModule } from './admin-routing.module';
import { AdminBuyersComponent } from './buyers/admin-buyers.component';
import { AdminBuyersService } from './buyers/admin-buyers.service';
import { CreateBuyerAccountRelationshipRadioList } from './buyers/create-buyer/create-buyer-account-relationship-radio-list.component';
import { AdminCreateBuyerComponent } from './buyers/create-buyer/create-buyer.component';
import { BuyerCompanyInformationComponent } from './buyers/manage-buyer/company-information/buyer-company-information.component';
import { AdminManageBuyerCompanyLocationComponent } from './buyers/manage-buyer/company-location/buyer-company-location.component';
import { AdminBuyerConfigurationIngestAllInvoicesComponent } from './buyers/manage-buyer/configuration/admin-buyer-configuration-ingest-all-invoices.component';
import { AdminBuyerConfigurationInforNexusEnabledComponent } from './buyers/manage-buyer/configuration/admin-buyer-configuration-infor-nexus-enabled.component';
import { AdminBuyerConfigurationMaxDaysEarlyComponent } from './buyers/manage-buyer/configuration/admin-buyer-configuration-max-days-early.component';
import { AdminBuyerConfigurationDecayingIneligibleComponent } from './buyers/manage-buyer/configuration/buyer-configuration-decaying-ineligible.component';
import { AdminBuyerConfigurationEligibilitySection } from './buyers/manage-buyer/configuration/buyer-configuration-eligibility-setting-section.component';
import { AdminBuyerConfigurationEligibilitySettings } from './buyers/manage-buyer/configuration/buyer-configuration-eligibility-setting.component';
import { AdminBuyerConfigurationGracePeriodComponent } from './buyers/manage-buyer/configuration/buyer-configuration-grace-period.component';
import { AdminBuyerConfigurationPaidFeesVisibilityComponent } from './buyers/manage-buyer/configuration/buyer-configuration-paid-fees-visibility.component';
import { AdminBuyerConfigurationPaidFeesComponent } from './buyers/manage-buyer/configuration/buyer-configuration-paid-fees.component';
import { AdminBuyerConfigurationPaidTransferFeesComponent } from './buyers/manage-buyer/configuration/buyer-configuration-paid-transfer-fees.component';
import { AdminBuyerConfigurationPastDueComponent } from './buyers/manage-buyer/configuration/buyer-configuration-past-due.component';
import { AdminBuyerConfigurationRevenueShareComponent } from './buyers/manage-buyer/configuration/buyer-configuration-revenue-share.component';
import { AdminBuyerConfigurationSectionComponent } from './buyers/manage-buyer/configuration/buyer-configuration-section.component';
import { AdminBuyerConfigurationTpmComponent } from './buyers/manage-buyer/configuration/buyer-configuration-tpm.component';
import { AdminManageBuyerConfigurationComponent } from './buyers/manage-buyer/configuration/buyer-configuration.component';
import { AdminManageBuyerConfigurationService } from './buyers/manage-buyer/configuration/buyer-configuration.service';
import { AdminBuyerConfigurationPayLaterComponent } from './buyers/manage-buyer/configuration/pay-later/buyer-configuration-pay-later.component';
import { AdminManageBuyerContactComponent } from './buyers/manage-buyer/contact/buyer-contact.component';
import { ManageBuyerComponent } from './buyers/manage-buyer/manage-buyer.component';
import { AdminManageBuyerConfirmChangesModalComponent } from './buyers/manage-buyer/modals/confirm-changes-modal.component';
import { ReviewBuyerCompanyContactComponent } from './buyers/manage-buyer/review-buyer/review-buyer-company-contact.component';
import { ReviewBuyerCompanyInformationComponent } from './buyers/manage-buyer/review-buyer/review-buyer-company-information.component';
import { ReviewBuyerCompanyLocationComponent } from './buyers/manage-buyer/review-buyer/review-buyer-company-location.component';
import { ReviewBuyerConfigurationSectionComponent } from './buyers/manage-buyer/review-buyer/review-buyer-configuration-section.component';
import { ReviewBuyerConfigurationComponent } from './buyers/manage-buyer/review-buyer/review-buyer-configuration.component';
import { BuyerReviewSectionComponent } from './buyers/manage-buyer/review-buyer/review-buyer-section.component';
import { AdminReviewBuyerComponent } from './buyers/manage-buyer/review-buyer/review-buyer.component';
import { ClientManagementComponent } from './client-management/client-management.component';
import { AdminDocumentsComponent } from './documents/documents.component';
import { RequestDocumentModalComponent } from './documents/request-document-modal/request-document-modal.component';
import { AdminInvoicesComponent } from './invoices/admin-invoices.component';
import { InvoiceService } from './invoices/invoice.service';
import { AdminProspectsComponent } from './prospects/admin-prospects.component';
import { AdminProspectsService } from './prospects/admin-prospects.service';
import { ReviewAdminContainer } from './prospects/dr-review-admin/dr-review-admin.component';
import { ReviewStageAdmin } from './prospects/dr-review-stage-admin/dr-review-stage-admin.component';
import { DynamicContainerAdmin } from './prospects/dynamic-container-admin/dynamic-container-admin.component';
import { ManageProspectComponent } from './prospects/manage-prospect/manage-prospect.component';
import { ManageProspectActivityComponent } from './prospects/manage-prospect/prospect-activity/prospect-activity.component';
import { ManageProspectDetailsComponent } from './prospects/manage-prospect/prospect-details/prospect-details.component';
import { MigrateProspectModalComponent } from './prospects/manage-prospect/prospect-migration/migrate-prospect-modal/migrate-prospect-modal.component';
import { ManageProspectMigrationComponent } from './prospects/manage-prospect/prospect-migration/prospect-migration.component';
import { ManageProspectUsersComponent } from './prospects/manage-prospect/prospect-users/prospect-users.component';
import { InviteProspectModalComponent } from './prospects/modals/invite-prospect-modal.component';
import { InitializeProspectModalComponent } from './prospects/modals/initialize-prospect-modal.component';
import { InviteUserModalComponent } from './prospects/modals/invite-user/invite-user-modal.component';
import { ProspectWatchersModalComponent } from './prospects/modals/prospect-watchers/prospect-watchers-modal.component';
import { MarkPurchaseRequestModalComponent } from './purchase-requests/mark-purchase-request-modal/mark-purchase-request-modal.component';
import { NewPurchaseRequestsModalComponent } from './purchase-requests/new-purchase-requests-modal/new-purchase-requests-modal.component';
import { AdminPurchaseRequestsComponent } from './purchase-requests/purchase-requests.component';
import { AdminSuppliersComponent } from './suppliers/admin-suppliers.component';
import { AdminSuppliersService } from './suppliers/admin-suppliers.service';
import { BulkActionConfirmationModalComponent } from './suppliers/bulk-actions/bulk-action-confirmation-modal.component';
import { AdminSuppliersBulkActionLayoutComponent } from './suppliers/bulk-actions/bulk-action-layout.component';
import { AdminSuppliersBulkActionsComponent } from './suppliers/bulk-actions/bulk-actions.component';
import { AdminSuppliersBulkActionsService } from './suppliers/bulk-actions/bulk-actions.service';
import { AdminSuppliersBulkEligibilityComponent } from './suppliers/bulk-actions/bulk-eligibility.component';
import { AdminSupplierBulkEnrollmentInviteComponent } from './suppliers/bulk-actions/bulk-enrollment-invite.component';
import { AdminSuppliersBulkFeeComponent } from './suppliers/bulk-actions/bulk-fee.component';
import { CreateSuppliersComponent } from './suppliers/create-suppliers/create-suppliers.component';
import { ManageSupplierImplementationAgreementComponent } from './suppliers/manage-supplier/implementation/implementation-cards/supplier-implementation-agreement.component';
import { ManageSupplierImplementationBuyerFeeComponent } from './suppliers/manage-supplier/implementation/implementation-cards/supplier-implementation-buyer-fee.component';
import { ManageSupplierImplementationEnrollmentInviteComponent } from './suppliers/manage-supplier/implementation/implementation-cards/supplier-implementation-enrollment-invite.component';
import { ManageSupplierImplementationFeeStructureComponent } from './suppliers/manage-supplier/implementation/implementation-cards/supplier-implementation-fee-structure.component';
import { ManageSupplierImplementationSummaryCardsComponent } from './suppliers/manage-supplier/implementation/implementation-cards/supplier-implementation-summary-cards.component';
import { ManageSupplierImplementationTransferFeesComponent } from './suppliers/manage-supplier/implementation/implementation-cards/supplier-implementation-transfer-fees.component';
import { ManageSupplierImplementationComponent } from './suppliers/manage-supplier/implementation/manage-supplier-implementation.component';
import { ManageSupplierInformationComponent } from './suppliers/manage-supplier/information/manage-supplier-information.component';
import { ManageSupplierComponent } from './suppliers/manage-supplier/manage-supplier.component';
import { ManageSupplierPaymentMethodsComponent } from './suppliers/manage-supplier/payment-methods/manage-supplier-payment-methods.component';
import { AgreementCompleteCheckModalComponent } from './suppliers/modals/agreement-complete-check-modal.component';
import { PaymentMethodStatusUpdateModalComponent } from './suppliers/modals/payment-method-status-update-modal.component';
import { SaveBuyerPaidEarlyPaymentFeesModalComponent } from './suppliers/modals/save-buyer-paid-early-payment-fees-modal.component';
import { SaveBuyerPaidTransferFeesModalComponent } from './suppliers/modals/save-buyer-paid-transfer-fees-modal.component';
import { SaveFeeStructureModalComponent } from './suppliers/modals/save-fee-structure-modal.component';
import { SendSupplierEnrollmentEmailModalComponent } from './suppliers/modals/send-supplier-enrollment-email-modal.component';
import { SupplierPrimaryContactModalComponent } from './suppliers/modals/supplier-primary-contact-modal.component';
import { SupplierResetEnrollmenttModalComponent } from './suppliers/modals/supplier-reset-enrollment-modal.componen';
import { ToggleScfEnabledModalComponent } from './suppliers/modals/toggle-scf-enabled-modal.component';
import { ToggleSupplierActivationModalComponent } from './suppliers/modals/toggle-supplier-activation-modal.component';
import { ToggleSupplierEligibilityModalComponent } from './suppliers/modals/toggle-supplier-eligibility-modal.component';
import { ToggleAlternativeAgreementModalComponent } from './suppliers/modals/toggle-using-alternative-agreement-modal.component';
import { VerifySupplierUploadModalComponent } from './suppliers/modals/verify-supplier-upload-modal.component';
import { AdminUserManagementComponent } from './user-management/user-management.component';
import { AdminSuppliersBulkEnableScfComponent } from './suppliers/bulk-actions/bulk-enable-scf.component';

export function createApollo(
    httpLink: HttpLink,
    settings: AppSettings,
    alertService: AlertService,
    errorMessageHelper: ErrorMessageHelper
): NamedOptions {
    const financingGraphQl = settings.httpApiServiceNameUrlMap['financingGql'] + '/v1/query';

    const errorLink = onError(() => {
        alertService.error(errorMessageHelper.GENERIC_ERROR);
    });

    return {
        invoiceGrapql: {
            link: ApolloLink.from([
                httpLink.create({ uri: 'https://dev.lsqlabs.com/inv-graphql/v1/query' })
            ]),
            cache: new InMemoryCache()
        },
        financingGraphQl: {
            link: ApolloLink.from([errorLink, httpLink.create({ uri: financingGraphQl })]),
            cache: new InMemoryCache(),
            defaultOptions: {
                watchQuery: {
                    notifyOnNetworkStatusChange: true,
                    fetchPolicy: 'no-cache'
                },
                query: {
                    notifyOnNetworkStatusChange: true,
                    fetchPolicy: 'no-cache'
                }
            }
        }
    };
}

@NgModule({
    declarations: [
        ClientManagementComponent,
        AdminUserManagementComponent,

        // Buyers
        AdminBuyersComponent,
        AdminCreateBuyerComponent,
        CreateBuyerAccountRelationshipRadioList,
        ManageBuyerComponent,
        AdminManageBuyerConfirmChangesModalComponent,
        BuyerCompanyInformationComponent,
        AdminManageBuyerCompanyLocationComponent,
        AdminManageBuyerConfigurationComponent,
        AdminBuyerConfigurationSectionComponent,
        AdminBuyerConfigurationRevenueShareComponent,
        AdminBuyerConfigurationDecayingIneligibleComponent,
        AdminBuyerConfigurationMaxDaysEarlyComponent,
        AdminBuyerConfigurationIngestAllInvoicesComponent,
        AdminBuyerConfigurationInforNexusEnabledComponent,
        AdminBuyerConfigurationGracePeriodComponent,
        AdminBuyerConfigurationPastDueComponent,
        AdminBuyerConfigurationPaidFeesComponent,
        AdminBuyerConfigurationPaidFeesVisibilityComponent,
        AdminBuyerConfigurationPaidTransferFeesComponent,
        AdminBuyerConfigurationEligibilitySection,
        AdminBuyerConfigurationEligibilitySettings,
        AdminBuyerConfigurationTpmComponent,
        AdminManageBuyerContactComponent,
        AdminReviewBuyerComponent,
        ReviewBuyerCompanyLocationComponent,
        AdminReviewBuyerComponent,
        BuyerReviewSectionComponent,
        ReviewBuyerCompanyInformationComponent,
        ReviewBuyerConfigurationComponent,
        ReviewBuyerCompanyContactComponent,
        ReviewBuyerConfigurationSectionComponent,
        AdminBuyerConfigurationPayLaterComponent,

        // Suppliers
        AdminSuppliersComponent,
        CreateSuppliersComponent,
        AdminSuppliersBulkActionsComponent,
        AdminSuppliersBulkActionLayoutComponent,
        AdminSuppliersBulkEligibilityComponent,
        AdminSuppliersBulkEnableScfComponent,
        AdminSuppliersBulkFeeComponent,
        AdminSupplierBulkEnrollmentInviteComponent,
        BulkActionConfirmationModalComponent,

        ManageSupplierComponent,
        ManageSupplierInformationComponent,
        ManageSupplierImplementationComponent,
        ManageSupplierImplementationFeeStructureComponent,
        ManageSupplierImplementationAgreementComponent,
        ManageSupplierImplementationEnrollmentInviteComponent,
        ManageSupplierPaymentMethodsComponent,
        ManageSupplierImplementationSummaryCardsComponent,
        ManageSupplierImplementationBuyerFeeComponent,
        ManageSupplierImplementationTransferFeesComponent,

        // Documents
        AdminDocumentsComponent,
        RequestDocumentModalComponent,

        // Prospects
        AdminProspectsComponent,
        ManageProspectComponent,
        ManageProspectDetailsComponent,
        ManageProspectMigrationComponent,
        ManageProspectActivityComponent,
        ManageProspectUsersComponent,
        InviteProspectModalComponent,
        InitializeProspectModalComponent,
        InviteUserModalComponent,
        MigrateProspectModalComponent,
        DynamicContainerAdmin,
        ReviewStageAdmin,
        ReviewAdminContainer,
        ProspectWatchersModalComponent,

        // Purchase Requests
        AdminPurchaseRequestsComponent,
        NewPurchaseRequestsModalComponent,
        MarkPurchaseRequestModalComponent,

        // Modals
        ToggleSupplierActivationModalComponent,
        ToggleSupplierEligibilityModalComponent,
        ToggleScfEnabledModalComponent,
        VerifySupplierUploadModalComponent,
        SendSupplierEnrollmentEmailModalComponent,
        SupplierPrimaryContactModalComponent,
        SaveFeeStructureModalComponent,
        PaymentMethodStatusUpdateModalComponent,
        ToggleAlternativeAgreementModalComponent,
        AgreementCompleteCheckModalComponent,
        SupplierResetEnrollmenttModalComponent,
        SaveBuyerPaidEarlyPaymentFeesModalComponent,
        SaveBuyerPaidTransferFeesModalComponent,
        // Invoices
        AdminInvoicesComponent
    ],
    imports: [
        AdminRoutingModule,
        LsqTablesModule.forChild(),
        AppCommonModule,
        SettingsModule,
        DrApplicationSharedComponentsModule,
        ApolloModule
    ],
    providers: [
        AdminSuppliersService,
        AdminSuppliersBulkActionsService,
        AdminBuyersService,
        AdminProspectsService,
        AdminManageBuyerConfigurationService,
        CheckableRowsServiceFactory,
        InvoiceService,
        {
            provide: APOLLO_NAMED_OPTIONS,
            useFactory: createApollo,
            deps: [HttpLink, AppSettings, AlertService, ErrorMessageHelper]
        }
    ]
})
export class AdminModule {
    constructor() {
        console.debug('Loading fasttrack-monolith/AdminModule.');
    }
}
